/**
 * Web counterpart to locationEngine.ts — same public surface
 * (getSnapshot/getCoords/getMovementMode/getPermissionStatus/getIsTracking/
 * getMarkerData/subscribe/start/ensureLocationPermission/pause/resume) so
 * useLocation.ts and LocationEngineProvider work unchanged on web, backed by
 * the browser Geolocation API instead of react-native-geolocation-service
 * (native-only, not imported here so it's never pulled into the web bundle).
 *
 * Movement-mode bucket-switch hysteresis and per-mode watch tuning from the
 * native engine are simplified to a single watchPosition call — map/feed
 * screens aren't part of the Phase 0 login-flow proof, so exact parity there
 * is a Phase 1 concern, not this file's job today.
 */
export type MovementMode = 'stationary' | 'walking' | 'cycling' | 'driving';

export type LocationPermissionStatus =
  | 'idle'
  | 'requesting'
  | 'granted'
  | 'denied'
  | 'blocked'
  | 'unavailable';

export type LocationCoords = { latitude: number; longitude: number };

export type LocationMarkerData = {
  latitude: number;
  longitude: number;
  heading: number | null;
  accuracy: number | null;
};

export type LocationSnapshot = {
  coords: LocationCoords | null;
  accuracy: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: number | null;
  permissionStatus: LocationPermissionStatus;
  isTracking: boolean;
  movementMode: MovementMode;
};

type Listener = () => void;

const STATIONARY_MAX_SPEED = 0.3;
const WALKING_MAX_SPEED = 2;
const CYCLING_MAX_SPEED = 7;

function classifyBucket(speedMetersPerSecond: number): MovementMode {
  if (speedMetersPerSecond < STATIONARY_MAX_SPEED) return 'stationary';
  if (speedMetersPerSecond < WALKING_MAX_SPEED) return 'walking';
  if (speedMetersPerSecond < CYCLING_MAX_SPEED) return 'cycling';
  return 'driving';
}

class LocationEngine {
  private snapshot: LocationSnapshot = {
    coords: null,
    accuracy: null,
    heading: null,
    speed: null,
    timestamp: null,
    permissionStatus: 'idle',
    isTracking: false,
    movementMode: 'stationary',
  };

  private listeners = new Set<Listener>();
  private notifyScheduled = false;
  private markerData: LocationMarkerData | null = null;
  private hasStarted = false;
  private wasTrackingBeforePause = false;
  private watchId: number | null = null;

  getSnapshot = (): LocationSnapshot => this.snapshot;
  getCoords = (): LocationCoords | null => this.snapshot.coords;
  getMovementMode = (): MovementMode => this.snapshot.movementMode;
  getPermissionStatus = (): LocationPermissionStatus => this.snapshot.permissionStatus;
  getIsTracking = (): boolean => this.snapshot.isTracking;
  getMarkerData = (): LocationMarkerData | null => this.markerData;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private patch(partial: Partial<LocationSnapshot>) {
    this.snapshot = { ...this.snapshot, ...partial };
    this.refreshMarkerDataCache();
    this.scheduleNotify();
  }

  private refreshMarkerDataCache() {
    const { coords, heading, accuracy } = this.snapshot;
    if (!coords) {
      this.markerData = null;
      return;
    }
    this.markerData = { latitude: coords.latitude, longitude: coords.longitude, heading, accuracy };
  }

  private scheduleNotify() {
    if (this.notifyScheduled) return;
    this.notifyScheduled = true;
    requestAnimationFrame(() => {
      this.notifyScheduled = false;
      this.listeners.forEach(listener => listener());
    });
  }

  /** Idempotent — safe to call multiple times (e.g. React StrictMode double-effect). */
  start = async (): Promise<void> => {
    if (this.hasStarted) return;
    this.hasStarted = true;
    await this.ensurePermissionAndWatch();
  };

  ensureLocationPermission = async (): Promise<LocationPermissionStatus> => {
    if (this.snapshot.permissionStatus === 'granted') return 'granted';
    return this.ensurePermissionAndWatch();
  };

  private ensurePermissionAndWatch(): Promise<LocationPermissionStatus> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.patch({ permissionStatus: 'unavailable' });
      return Promise.resolve('unavailable');
    }

    this.patch({ permissionStatus: 'requesting' });
    return new Promise<LocationPermissionStatus>(resolve => {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.patch({ permissionStatus: 'granted' });
          this.handleRawFix(position);
          this.startWatch();
          resolve('granted');
        },
        error => {
          const status: LocationPermissionStatus =
            error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable';
          this.patch({ permissionStatus: status });
          resolve(status);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
      );
    });
  }

  /** Pause on tab hidden — keeps the last known snapshot, clears the watch. */
  pause = (): void => {
    if (!this.snapshot.isTracking) return;
    this.wasTrackingBeforePause = true;
    this.clearActiveWatch();
    this.patch({ isTracking: false });
  };

  /** Resume on tab visible — restarts the watch only if it was previously active. */
  resume = (): void => {
    if (!this.hasStarted) return;
    if (this.snapshot.permissionStatus !== 'granted') return;
    if (!this.wasTrackingBeforePause) return;
    this.startWatch();
  };

  private clearActiveWatch() {
    if (this.watchId !== null && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  private startWatch() {
    this.clearActiveWatch();
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    this.watchId = navigator.geolocation.watchPosition(
      position => this.handleRawFix(position),
      error => {
        if (__DEV__) console.warn('[LocationEngine.web] watch error', error.code, error.message);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
    );
    this.patch({ isTracking: true });
  }

  private handleRawFix(position: GeolocationPosition) {
    const { latitude, longitude, accuracy, heading, speed } = position.coords;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    if (latitude === 0 && longitude === 0) return;

    const nextCoords: LocationCoords = { latitude, longitude };
    const instantaneousSpeed = typeof speed === 'number' && speed >= 0 ? speed : 0;

    this.patch({
      coords: nextCoords,
      accuracy,
      heading: heading ?? this.snapshot.heading,
      speed: instantaneousSpeed,
      timestamp: position.timestamp,
      movementMode: classifyBucket(instantaneousSpeed),
    });
  }
}

export const locationEngine = new LocationEngine();
